import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { FacebookRegularIcon, GroupsIcon } from '@change/ds-icons';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { FacebookPostDestination } from '../../types';
import { CopiedPill } from '../CopiedPill';
import { RadioCard } from '../RadioCard';

type Props = {
	/** The post preview, drawn above the question and the cards. It stays the same for both cards. */
	preview: React.ReactNode;
	/** Whether the Facebook tap that opened the sheet copied the post. Shows the copied pill. */
	copied: boolean;
	copySuccessLabel: string;
	value: FacebookPostDestination;
	onChange: (destination: FacebookPostDestination) => void;
};

const IconTile: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<span className="bg-fill-selected-weaker text-icon-selected flex size-10 items-center justify-center rounded-md">
		{children}
	</span>
);

/**
 * The `variant_2` first step: the `variant_1` screen (the copied pill when the Facebook tap copied the
 * post, then the post preview), with a choice below it to post to the supporter's own feed or to a
 * Facebook group.
 */
export const DestinationChoice: React.FC<Props> = ({ preview, copied, copySuccessLabel, value, onChange }) => {
	const { translate } = useI18n();

	return (
		<div className="flex w-full flex-col items-center gap-4" data-testid="facebook-post-sheet-destination">
			{copied && <CopiedPill label={copySuccessLabel} />}
			{preview}
			<fieldset className="m-0 flex w-full flex-col gap-3 border-0 p-0">
				<legend className="mb-3 p-0">
					<Text as="span" size="medium" fontWeight="bold" className="text-typography-weak">
						{translate('corgi.psf.promote_or_share.facebook_post_sheet.destination_question')}
					</Text>
				</legend>
				<RadioCard
					name="facebook-post-destination"
					value="feed"
					checked={value === 'feed'}
					onChange={() => onChange('feed')}
					leading={
						<IconTile>
							<FacebookRegularIcon size="small" />
						</IconTile>
					}
					label={translate('corgi.psf.promote_or_share.facebook_post_sheet.destination_feed_label')}
					description={translate('corgi.psf.promote_or_share.facebook_post_sheet.destination_feed_description')}
					testId="facebook-post-sheet-destination-feed"
				/>
				<RadioCard
					name="facebook-post-destination"
					value="group"
					checked={value === 'group'}
					onChange={() => onChange('group')}
					leading={
						<IconTile>
							<GroupsIcon size="small" />
						</IconTile>
					}
					label={translate('corgi.psf.promote_or_share.facebook_post_sheet.destination_group_label')}
					description={translate('corgi.psf.promote_or_share.facebook_post_sheet.destination_group_description')}
					testId="facebook-post-sheet-destination-group"
				/>
			</fieldset>
		</div>
	);
};
